'use client'

import { Button } from '@vinted/web-ui'
import { useRef, useState } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { navigateToPage, reloadPage } from '@marketplace-web/shared/browser-util'
import {
  CONVERSATION_URL,
  TransactionModel,
} from '@marketplace-web/messaging/conversation-messaging-data'

import {
  ComplaintResolutionAction,
  ComplaintResolutionChoice,
  getComplaint,
  ComplaintModel,
  transformComplaintDto,
} from '@marketplace-web/complaints/complaints-data'
import { ConversationActionProps } from '@marketplace-web/messaging/conversation-action-data'
import { ConversationActionButton } from '@marketplace-web/messaging/conversation-action-feature'

import GenericErrorModal from '../GenericErrorModal'
import RequestReturn, { RequestReturnRef } from '../RequestReturn'
import RequestReturnDetailsProvider from '../RequestReturn/RequestReturnProvider'
import ResolveIssueButton from '../ResolveIssueButton'
import ChooseComplaintResolutionDialog from './ChooseComplaintResolutionDialog'
import PartialRefundOfferDialog from './PartialRefundOfferDialog'

enum ChooseResolutionActiveDialog {
  ChooseResolutionDialog,
  OfferPartialRefundDialog,
  FullRefundDialog,
  RequestToReturnDialog,
  ErrorDialog,
  None,
}

type OwnProps = {
  complaint?: ComplaintModel | null
  complaintId?: string | null
  transaction?: TransactionModel | null
}

type Props = Partial<ConversationActionProps> & OwnProps

const ChooseComplaintResolution = ({
  primary,
  title,
  inverse,
  theme,
  size,
  complaint: preFetchedComplaint,
  complaintId, // TODO: change to transactionId and getSnadComplaint after no more old complaints
  transaction, // TODO: remove after no more old complaints
}: Props) => {
  const requestReturnRef = useRef<RequestReturnRef>(null)

  const [activeDialog, setActiveDialog] = useState(ChooseResolutionActiveDialog.None)

  const messageThreadUrl = preFetchedComplaint?.userMsgThreadId
    ? CONVERSATION_URL(preFetchedComplaint?.userMsgThreadId)
    : undefined

  const {
    fetch: fetchSnadComplaint,
    transformedData: fetchedComplaint,
    isLoading: isComplaintLoading,
  } = useFetch(getComplaint, transformComplaintDto)

  const fetchComplaint = async () => {
    if (preFetchedComplaint || !complaintId) return

    const response = await fetchSnadComplaint(complaintId)

    if ('errors' in response) {
      setActiveDialog(ChooseResolutionActiveDialog.ErrorDialog)
    }
  }

  const complaint = preFetchedComplaint || fetchedComplaint
  const complaintResolutionActions = complaint?.resolutions?.find(
    resolutions => resolutions.action === ComplaintResolutionAction.ChooseResolution,
  )?.resolutions

  const handleDialogClose = () => {
    setActiveDialog(ChooseResolutionActiveDialog.None)
  }

  const handleErrorDialogClose = () => {
    handleDialogClose()

    if (messageThreadUrl) {
      navigateToPage(messageThreadUrl)

      return
    }

    reloadPage()
  }

  const handleChooseResolutionDialogOpen = () => {
    setActiveDialog(ChooseResolutionActiveDialog.ChooseResolutionDialog)
  }

  const clickChooseResolutionButton = () => {
    fetchComplaint()
    handleChooseResolutionDialogOpen()
  }

  const openDialog = (resolution: ComplaintResolutionChoice) => {
    switch (resolution) {
      case ComplaintResolutionChoice.OfferPartialRefund:
        setActiveDialog(ChooseResolutionActiveDialog.OfferPartialRefundDialog)
        break
      case ComplaintResolutionChoice.Refund:
        setActiveDialog(ChooseResolutionActiveDialog.FullRefundDialog)
        break
      case ComplaintResolutionChoice.RequestToReturn:
        setActiveDialog(ChooseResolutionActiveDialog.RequestToReturnDialog)
        if (requestReturnRef.current) {
          requestReturnRef.current.handleReturnModalOpen()
        }
        break
      default:
        break
    }
  }

  return (
    <>
      <ConversationActionButton
        text={title}
        primary={primary}
        size={size || Button.Size.Medium}
        theme={theme}
        inverse={inverse}
        onClick={clickChooseResolutionButton}
        testId="choose-complaint-resolution-conversation-action"
      />
      <ChooseComplaintResolutionDialog
        isOpen={activeDialog === ChooseResolutionActiveDialog.ChooseResolutionDialog}
        transactionId={complaint?.transactionId?.toString()}
        complaintId={complaintId || complaint?.id?.toString()}
        complaintResolutionActions={complaintResolutionActions}
        isComplaintLoading={isComplaintLoading}
        onDialogOpen={openDialog}
        onClose={handleDialogClose}
      />
      <PartialRefundOfferDialog
        isOpen={activeDialog === ChooseResolutionActiveDialog.OfferPartialRefundDialog}
        url={messageThreadUrl}
        complaint={complaint}
        transaction={transaction}
        onSubmitError={() => setActiveDialog(ChooseResolutionActiveDialog.ErrorDialog)}
        onClose={handleDialogClose}
      />
      <RequestReturnDetailsProvider>
        <RequestReturn
          ref={requestReturnRef}
          isChooseResolutionFlow
          complaint={complaint}
          url={messageThreadUrl}
          conversationTransactionId={transaction?.id}
        />
      </RequestReturnDetailsProvider>
      <ResolveIssueButton
        isChooseResolutionFlow
        complaint={complaint}
        url={messageThreadUrl}
        shouldOpen={activeDialog === ChooseResolutionActiveDialog.FullRefundDialog}
      />
      <GenericErrorModal
        isShown={activeDialog === ChooseResolutionActiveDialog.ErrorDialog}
        onClose={handleErrorDialogClose}
      />
    </>
  )
}

export default ChooseComplaintResolution
